// src/components/layout/StepperHeader.jsx

import React, { useState } from 'react';

const StepperHeader = ({ 
  title = "Inspection Bot", 
  currentStep = 1, 
  showSaveDraft = true,
  onSaveDraft,
  isDarkMode = false,
  isSaving = false,
  toggleDarkMode
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const steps = [
    { number: 1, label: 'Select Template' },
    { number: 2, label: 'Record Type' },
    { number: 3, label: 'Trigger Location' },
    { number: 4, label: 'Inspection Type' },
    { number: 5, label: 'Inspection Result' },
    { number: 6, label: 'Contact Types' },
    { number: 7, label: 'Summary' },
    { number: 8, label: 'Review & Publish' },
  ];

  const navigationItems = [
    { label: 'Home', icon: '🏠', path: '/mainscreen' },
    { label: 'My Apps', icon: '📱', path: '/mainscreen', active: true },
    { label: 'Usage', icon: '📊', path: '/mainscreen' },
    { label: 'Settings', icon: '⚙️', path: '/mainscreen' },
  ];

  const handleSaveDraft = () => {
    if (onSaveDraft) {
      onSaveDraft();
    } else {
      console.log('Saving draft...');
    }
  };

  const handleNavigationClick = (path) => {
    window.location.href = path;
    setIsSidebarOpen(false);
  };

  return (
    <>
      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[90]"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 left-0 h-full w-64 z-[95] transform transition-transform duration-300 ease-in-out ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r shadow-lg`}>
        
        {/* Sidebar Header */}
        <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Navigation
            </h2>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className={`p-1 rounded-md transition-colors ${
                isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation Items */}
        <div className="p-4">
          <nav className="space-y-2">
            {navigationItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleNavigationClick(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                  item.active
                    ? isDarkMode 
                      ? 'bg-[#7376B8] text-white' 
                      : 'bg-[#100E27] text-white'
                    : isDarkMode
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Page Header with Title and Controls */}
      <div className={`fixed top-[70px] left-0 right-0 z-[80] shadow-sm border-b transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#DBDCED]'
      }`}>
        <div className="max-w-[1200px] mx-auto px-8 py-3 flex justify-between items-center">
          
          {/* Left Side - Hamburger Menu + Title */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className={`p-2 rounded-md transition-colors ${
                isDarkMode 
                  ? 'hover:bg-gray-700 text-gray-300' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
              aria-label="Open navigation menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <h1 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-[#100E27]'}`}>
              {title}
            </h1>
          </div>

          {/* Right Side - Save Draft + Dark Mode Toggle */}
          <div className="flex items-center gap-3">
            {showSaveDraft && (
              <button
                onClick={handleSaveDraft}
                disabled={isSaving}
                className={`flex items-center gap-2 px-5 py-2.5 border rounded-lg text-sm font-medium transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' 
                    : 'bg-[#F5F5FA] border-[#DBDCED] text-[#41425A] hover:bg-[#DBDCED]'
                } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSaving ? (
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16L21 8V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {isSaving ? 'Saving...' : 'Save Draft'}
              </button>
            )}

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`p-2.5 rounded-md transition-colors ${
                isDarkMode 
                  ? 'hover:bg-gray-700 text-yellow-400' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Progress Steps - Positioned right after the title bar */}
      <div className="fixed top-[124px] left-0 right-0 z-[70] py-4">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="flex items-center justify-center gap-4 px-5">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className={`flex items-center gap-2 cursor-pointer transition-all duration-300 px-3 py-2 rounded-lg ${
                  step.number === currentStep 
                    ? isDarkMode ? 'bg-[#7376B8] text-white' : 'bg-[#100E27] text-white'
                    : isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-[#DBDCED]'
                }`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                    step.number === currentStep
                      ? 'bg-white text-[#100E27]'
                      : step.number < currentStep
                      ? isDarkMode ? 'bg-green-600 text-white' : 'bg-green-600 text-white'
                      : isDarkMode 
                        ? 'bg-gray-600 text-gray-300'
                        : 'bg-[#DBDCED] text-[#41425A]'
                  }`}>
                    {step.number < currentStep ? '✓' : step.number}
                  </div>
                  {step.number === currentStep && (
                    <span className="text-sm font-medium whitespace-nowrap">{step.label}</span>
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-3 h-px ${
                    step.number < currentStep
                      ? 'bg-green-500'
                      : isDarkMode ? 'bg-gray-600' : 'bg-[#DBDCED]'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default StepperHeader;